import React, { useState, useEffect } from "react";
import { Modal, Table, ProgressBar, Alert, Form } from "react-bootstrap";
import * as XLSX from "xlsx";
import { Button } from "../../Common";
import {
    createNewInvestingPartner,
    PartnerConfigList,
    UpdatePartnerConfig,
} from "../../api";
import { validatePartnerForm } from "../../utils/validation";
import styles from "./partners.module.css";
import { CookieUtils } from "../../axios.config";

const BulkCreatePartner = ({ show, onClose, onSuccess, userName, existingPartners }) => {
    const ALLOWED_SCOPES = [
        "cancel-stock-order", "create-etf-order", "create-fund-order", "create-lrs",
        "create-redemption", "create-stack-order", "create-stock-order", "create-user",
        "get-instr", "get-lrs", "get-order", "get-position", "get-redemption",
        "get-stack", "get-stmt", "get-user", "kyc", "subscribe-plan", "update-user",
        "price", "price-chart"
    ];

    const [file, setFile] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [validationResults, setValidationResults] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [configTemplate, setConfigTemplate] = useState(null);
    const [statusMessage, setStatusMessage] = useState("");
    const [uploadSummary, setUploadSummary] = useState(null);

    // Mapping of Internal Keys to Verbose Excel Headers
    const HEADER_MAP = {
        name: "Partner Name",
        code: "Partner Code (Unique and alphabets only)",
        isWhiteLabelled: "Is Whitelabelled? (TRUE/FALSE)",
        isApiPartner: "Is API Partner? (TRUE/FALSE)",
        isReferralPartner: "Is Referral Partner? (TRUE/FALSE)",
        host: "Host URL (Unique and only applicable for whitelabelled partners)",
        description: "Description",
        // Config Keys Mapping (assumed internal keys)
        enableReferralCode: "Enable user referral code (TRUE only for referral partner)",
        POCName: "Point of contact name at partner end",
        POCEmail: "Point of contact email at partner end",
        POCPhone: "Point of contact phone at partner end"
    };

    const REVERSE_HEADER_MAP = Object.fromEntries(Object.entries(HEADER_MAP).map(([k, v]) => [v, k]));

    const getCurrentUsername = () => {
        if (userName) return userName;
        const cookieUserName = CookieUtils.get("username");
        if (cookieUserName) {
            try {
                return JSON.parse(cookieUserName);
            } catch {
                return cookieUserName;
            }
        }
        return "system";
    };

    useEffect(() => {
        if (show) {
            fetchConfigTemplate();
            resetState();
        }
    }, [show]);

    const fetchConfigTemplate = async () => {
        try {
            const response = await PartnerConfigList("partner");
            if (response.status === 200 && response.data?.data) {
                setConfigTemplate(response.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch config template", error);
        }
    };

    const resetState = () => {
        setFile(null);
        setParsedData([]);
        setValidationResults([]);
        setUploading(false);
        setProgress(0);
        setStatusMessage("");
        setUploadSummary(null);
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            resetState(); // Reset state for re-upload
            setFile(selectedFile);
            parseExcel(selectedFile);
            // Clear input so same file can be re-selected if needed
            e.target.value = "";
        }
    };

    const parseExcel = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Get headers for validation
            const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            const headers = rows[0] || [];
            const requiredHeaders = Object.values(HEADER_MAP);
            const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

            if (missingHeaders.length > 0) {
                setValidationResults([{
                    row: "-",
                    isValid: false,
                    errors: { form: `Template mismatch: Required headers are missing or incorrect.` },
                    data: {},
                    formatted: {}
                }]);
                setParsedData([]);
                return;
            }

            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
            setParsedData(jsonData);
            validateRows(jsonData);
        };
        reader.readAsArrayBuffer(file);
    };

    const validateRows = (data) => {
        if (data.length === 0) {
            setValidationResults([{
                row: "-",
                isValid: false,
                errors: { form: "The uploaded file contains no data rows." },
                data: {},
                formatted: {}
            }]);
            return;
        }

        if (data.length > 10) {
            setValidationResults([{
                row: "-",
                isValid: false,
                errors: { form: "Limit exceeded. Maximum 10 partners per upload allowed." },
                data: {},
                formatted: {}
            }]);
            return;
        }

        const seenCodes = new Set();
        const seenHosts = new Set();

        const results = data.map((row, index) => {
            const formData = mapRowToFormData(row);
            const errors = {};

            // Helper to get raw value from config columns
            const getRawValue = (key) => row[HEADER_MAP[key]];

            // 1. Partner Code Validation
            if (!formData.code) {
                errors.code = "Partner Code is required.";
            } else if (!/^[A-Z]+$/i.test(formData.code)) {
                errors.code = "Code must be alphabets only.";
            }

            // Contact Validation (Mandatory as per request)
            const POCName = getRawValue("POCName");
            if (!POCName || String(POCName).trim() === "") {
                errors.POCName = "Point of contact name is required.";
            }

            const POCEmail = getRawValue("POCEmail");
            if (!POCEmail || String(POCEmail).trim() === "") {
                errors.POCEmail = "Point of contact email is required.";
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(POCEmail)) {
                errors.POCEmail = "Invalid Point of Contact Email.";
            }

            const POCPhone = getRawValue("POCPhone");
            if (!POCPhone || String(POCPhone).trim() === "") {
                errors.POCPhone = "Point of contact phone is required.";
            } else {
                const phoneStr = String(POCPhone).trim();
                if (!/^[6-9][0-9\s\-+]*$/.test(phoneStr)) {
                    errors.POCPhone = "Phone must start with 6, 7, 8, or 9 and contain only digits, spaces, dashes, or plus signs.";
                }
            }

            // 2. Partner Type Validation
            if (formData.type.length === 0) {
                errors.type = "At least one Partner Type (Whitelabelled/API/Referral) must be TRUE.";
            }

            const isWhiteLabel = formData.type.includes("whiteLabelled");
            const isApi = formData.type.includes("api");
            const isReferral = formData.type.includes("referral");

            // 3. Host Validation
            if (isWhiteLabel && !formData.host) {
                errors.host = "Host is required for WhiteLabelled partners.";
            }
            if (!isWhiteLabel && formData.host) {
                // Requirement: "for api partner, we should not provide host url"
                // Generalizing: If NOT whitelabelled, host should definitely be empty.
                errors.host = "Host URL should only be provided for WhiteLabelled partners.";
            }

            // 4. Referral Code Validation
            // "Enable referral code is valid only for referral partner"
            const enableReferralVal = getRawValue("enableReferralCode");
            const isReferralEnabled = String(enableReferralVal).toLowerCase() === "true";

            if (isReferralEnabled && !isReferral) {
                errors.referralConfig = "Enable user referral code must be FALSE for non-Referral partners.";
            }
            if (isReferral && !isReferralEnabled) {
                errors.referralConfig = "Enable user referral code must be TRUE for Referral partners.";
            }



            // Standard Validation
            // We skip standard validatePartnerForm for basic fields as we covered bespoke rules above 
            // but might still need 'name' and 'description' check.
            if (!formData.name) errors.name = "Partner Name is required.";
            if (!formData.description) errors.description = "Description is required.";


            // Check for duplicates in existing partners
            if (existingPartners) {
                const codeExists = existingPartners.some(p => p.code === formData.code);
                if (codeExists) {
                    errors.code = "Partner Code already exists.";
                }

                const nameExists = existingPartners.some(p => p.name === formData.name);
                if (nameExists) {
                    errors.name = "Partner Name already exists.";
                }

                if (formData.host) {
                    const hostExists = existingPartners.some(p => p.host === formData.host);
                    if (hostExists) {
                        errors.host = "Host already exists.";
                    }
                }
            }

            // Check for duplicates within the current file
            if (seenCodes.has(formData.code)) {
                errors.code = errors.code ? errors.code + " Duplicate in file." : "Duplicate Code in file.";
            }
            if (formData.code) seenCodes.add(formData.code);

            if (formData.host) {
                if (seenHosts.has(formData.host)) {
                    errors.host = errors.host ? errors.host + " Duplicate in file." : "Duplicate Host in file.";
                }
                seenHosts.add(formData.host);
            }

            const isValid = Object.keys(errors).length === 0;
            return { row: index + 2, isValid, errors, data: row, formatted: formData };
        });
        setValidationResults(results);
    };

    const mapRowToFormData = (row) => {
        // Helper to get value using verbose header OR internal key
        const getValue = (internalKey) => {
            return row[HEADER_MAP[internalKey]];
        };

        const types = [];
        if (String(getValue("isWhiteLabelled")).toLowerCase() === "true") types.push("whiteLabelled");
        if (String(getValue("isApiPartner")).toLowerCase() === "true") types.push("api");
        if (String(getValue("isReferralPartner")).toLowerCase() === "true") types.push("referral");

        return {
            name: getValue("name"),
            code: String(getValue("code") || "").toUpperCase(),
            host: getValue("host"),
            description: getValue("description"),
            status: "inactive",
            type: types,
            scopes: ALLOWED_SCOPES,
            isWhiteLabel: types.includes("whiteLabelled"),
            _rawData: row
        };
    };

    const handleDownloadTemplate = () => {
        const fixedHeaders = [
            HEADER_MAP.name,
            HEADER_MAP.code,
            HEADER_MAP.isWhiteLabelled,
            HEADER_MAP.isApiPartner,
            HEADER_MAP.isReferralPartner,
            HEADER_MAP.host,
            HEADER_MAP.description,
            HEADER_MAP.enableReferralCode,
            HEADER_MAP.POCName,
            HEADER_MAP.POCEmail,
            HEADER_MAP.POCPhone
        ];

        const ws = XLSX.utils.aoa_to_sheet([fixedHeaders]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "partner_upload_template.xlsx");
    };

    const handleUpload = async () => {
        setUploading(true);
        let successCount = 0;
        let failCount = 0;
        const failedRows = [];
        const results = []; // Array to store { row, name, code, status, message }

        const validRows = validationResults.filter((r) => r.isValid);
        const totalSteps = validRows.length;

        for (let i = 0; i < validRows.length; i++) {
            const item = validRows[i];
            const { formatted, data } = item;

            setStatusMessage(`Processing ${formatted.name} (${i + 1}/${totalSteps})...`);

            try {
                // 1. Create Partner
                let partnerCode = formatted.code;
                const apiData = {
                    name: formatted.name,
                    code: partnerCode,
                    host: formatted.host,
                    description: formatted.description,
                    status: formatted.status,
                    type: formatted.type,
                    createdBy: getCurrentUsername(),
                    scopes: formatted.scopes,
                };

                const createResponse = await createNewInvestingPartner(apiData);

                if (createResponse?.status === 200 || createResponse?.code === 200) {
                    // 2. Update Configs
                    try {
                        await updatePartnerConfigurations(partnerCode, data);
                        successCount++;
                        results.push({ ...item, status: "Success", message: "Partner created successfully" });
                    } catch (configError) {
                        // Partner created but config failed
                        successCount++; // Still count as success for partner creation? Or partial?
                        // Let's mark it as Success but with warning, or distinct status.
                        // For now simplicity: Success (Config Failed)
                        results.push({ ...item, status: "Warning", message: "Partner created, but config update failed: " + configError.message });
                    }
                } else {
                    const errorMsg = createResponse?.data?.message || createResponse?.message || "Creation failed";
                    throw new Error(errorMsg);
                }

            } catch (error) {
                failCount++;
                let errorMsg = error.message;
                // specialized handling for 400 if axios throws it
                if (error.response && error.response.data && error.response.data.message) {
                    errorMsg = error.response.data.message;
                }

                results.push({ ...item, status: "Failed", message: errorMsg });
                failedRows.push(`Row ${item.row}: ${errorMsg}`);
            }

            setProgress(Math.round(((i + 1) / totalSteps) * 100));
        }

        setUploading(false);
        setUploadSummary({ success: successCount, failed: failCount, errors: failedRows, results: results });
        if (successCount > 0) {
            // onSuccess(); // Do not close automatically
        }
    };

    const updatePartnerConfigurations = async (partnerCode, rowData) => {
        if (!configTemplate) return;

        const modules = Object.keys(configTemplate).filter(key => Array.isArray(configTemplate[key]));

        for (const module of modules) {
            const moduleFields = configTemplate[module];
            const configsToUpdate = [];

            moduleFields.forEach(field => {
                // Try verbose header first, then internal key
                const verboseHeader = HEADER_MAP[field.keyName];
                let value = undefined; // Reset for each field

                // Only try to read from Excel if we have a mapping for this key AND it exists in row
                if (verboseHeader && rowData[verboseHeader] !== undefined) {
                    value = rowData[verboseHeader];
                }

                // --- Inject Defaults and Fallbacks ---
                const moduleName = module.toLowerCase();

                // Branding Module Defaults
                if (moduleName === "branding") {
                    const BRANDING_DEFAULTS = {
                        shortLogoUrl: "https://prod-assets.stockal.com/partners/image%20(93).png",
                        favicon: "https://images.stockal.com/stockal/favicon.ico",
                        favicon_32: "https://images.stockal.com/stockal/favicon-32x32.png",
                        favicon_192: "https://images.stockal.com/stockal/android-chrome-192x192.png",
                        faviconApple: "https://images.stockal.com/stockal/apple-touch-icon.png",
                        partnerLogoUrl: "https://prod-assets.stockal.com/partners/image%20(92).png",
                        logo: "https://prod-assets.stockal.com/partners/image%20(93).png" // Fallback for 'logo' key
                    };
                    if (BRANDING_DEFAULTS[field.keyName]) {
                        value = BRANDING_DEFAULTS[field.keyName];
                    }
                }

                // Contact Module Fallbacks
                if (moduleName === "contact") {
                    if (field.keyName === "POCEmail" && (!value || value === "")) {
                        value = process.env.REACT_APP_DEFAULT_POC_EMAIL;
                    }
                    if (field.keyName === "POCPhone" && (!value || value === "")) {
                        value = process.env.REACT_APP_DEFAULT_POC_PHONE;
                    }
                }

                // Features Module
                if (moduleName === "features" && field.keyName === "availablePlans") {
                    value = process.env.REACT_APP_DEFAULT_AVAILABLE_PLANS;
                }

                // If value is still missing, utilize the field's default value from template
                if (value === undefined || value === "") {
                    value = field.defaultValue;
                }

                if (value !== undefined && value !== null && value !== "") {
                    // Format value based on type
                    if (field.fieldType === "BOOLEAN") {
                        const s = String(value).toLowerCase();
                        value = s === "true" || s === "1" || s === "yes";
                    } else if (field.fieldType === "NUMBER") {
                        value = Number(value);
                    } else if (field.fieldType === "ARRAY" || field.fieldType === "MULTI-DROPDOWN") {
                        if (typeof value === 'string') {
                            value = value.split(',').map(v => v.trim());
                        }
                    }

                    configsToUpdate.push({
                        key: field.keyName,
                        value: value,
                        source_table: field.sourceTable
                    });
                }
            });

            if (configsToUpdate.length > 0) {
                const payload = {
                    partnerCd: partnerCode,
                    module: module.toLowerCase(),
                    updatedBy: getCurrentUsername(),
                    status: "ACTIVE",
                    configs: configsToUpdate
                };
                await UpdatePartnerConfig(payload);
            }
        }
    };

    const handleClose = () => {
        if (uploading) return;
        if (uploadSummary && uploadSummary.success > 0) {
            onSuccess(); // Triggers refresh in parent
        } else {
            onClose(); // Just close
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static" keyboard={!uploading}>
            <Modal.Header style={{ backgroundColor: "#343434", color: "white", padding: "10px 20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <Modal.Title style={{ fontSize: "18px" }}>Bulk Create Partners</Modal.Title>
                    {!uploading && (
                        <span onClick={handleClose} style={{ cursor: "pointer", fontSize: "20px" }}>&times;</span>
                    )}
                </div>
            </Modal.Header>
            <Modal.Body style={{ backgroundColor: "#28282B", color: "white" }}>
                {!uploadSummary ? (
                    <>
                        <div className={styles.inputDiv} style={{ marginBottom: "20px" }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label style={{ fontSize: "14px" }}>Select Excel File</label>
                                <Button
                                    onClick={handleDownloadTemplate}
                                    size="sm"
                                    variant="outline-secondary"
                                    title="Download Template"
                                    style={{ fontSize: "12px", padding: "4px 8px", minWidth: "auto", height: "auto" }}
                                />
                            </div>

                            <Form.Control
                                type="file"
                                accept=".xlsx, .xls"
                                onChange={handleFileChange}
                                disabled={uploading}
                                size="sm"
                                style={{ backgroundColor: "#3b3b3b", color: "white", border: "1px solid #555" }}
                            />
                        </div>

                        {parsedData.length > 0 && (
                            <>
                                <div style={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto", marginBottom: "20px" }}>
                                    <Table striped bordered hover variant="dark" size="sm" style={{ fontSize: "12px" }}>
                                        <thead>
                                            <tr>
                                                <th>Row</th>
                                                <th>Partner Name</th>
                                                <th>Partner Code</th>
                                                <th>Validation</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {validationResults.map((res, idx) => (
                                                <tr key={idx}>
                                                    <td>{res.row}</td>
                                                    <td>{res.data[HEADER_MAP.name] || res.data["name"]}</td>
                                                    <td>{res.data[HEADER_MAP.code] || res.data["code"]}</td>
                                                    <td>
                                                        {res.isValid ?
                                                            <span style={{ color: "#4caf50" }}>Valid</span> :
                                                            <span style={{ color: "#ff6b6b" }}>
                                                                {Object.values(res.errors).join(", ")}
                                                            </span>
                                                        }
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {uploading && (
                                    <div style={{ marginBottom: "20px" }}>
                                        <div style={{ color: "#ff6b6b", marginBottom: "10px", fontWeight: "bold", fontSize: "14px", display: "flex", alignItems: "center" }}>
                                            <span style={{ marginRight: "8px", fontSize: "18px" }}>⚠️</span>
                                            Partner onboarding in progress... Do not close the browser
                                        </div>
                                        <p style={{ fontSize: "12px" }}>{statusMessage}</p>
                                        <ProgressBar now={progress} label={`${progress}%`} animated variant="success" style={{ height: "10px", fontSize: "10px" }} />
                                    </div>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <div>
                        <Alert variant="info" style={{ padding: "10px", fontSize: "14px" }}>
                            <strong>Upload Complete</strong>
                            <div>Successfully Created: {uploadSummary.success}</div>
                            <div>Failed: {uploadSummary.failed}</div>
                        </Alert>

                        <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
                            <Table striped bordered hover variant="dark" size="sm" style={{ fontSize: "12px" }}>
                                <thead>
                                    <tr>
                                        <th>Row</th>
                                        <th>Partner Name</th>
                                        <th>Partner Code</th>
                                        <th>Status</th>
                                        <th>Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {uploadSummary.results.map((res, idx) => (
                                        <tr key={idx}>
                                            <td>{res.row}</td>
                                            <td>{res.formatted.name}</td>
                                            <td>{res.formatted.code}</td>
                                            <td>
                                                {res.status === "Success" && <span style={{ color: "#4caf50" }}>Success</span>}
                                                {res.status === "Warning" && <span style={{ color: "#ff9800" }}>Warning</span>}
                                                {res.status === "Failed" && <span style={{ color: "#ff6b6b" }}>Failed</span>}
                                            </td>
                                            <td>{res.message}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}
            </Modal.Body >
            <Modal.Footer style={{ backgroundColor: "#343434", borderTop: "1px solid #444", padding: "10px" }}>
                <Button onClick={handleClose} title="Close" disabled={uploading} style={{ backgroundColor: "transparent", border: "1px solid white", padding: "5px 15px", fontSize: "14px" }}>
                    Close
                </Button>
                {!uploadSummary && parsedData.length > 0 && (
                    <Button
                        onClick={handleUpload}
                        title={uploading ? "Uploading..." : "Upload"}
                        disabled={uploading || validationResults.some(r => !r.isValid)}
                        loading={uploading}
                        style={{ padding: "5px 20px", fontSize: "14px" }}
                    />
                )}
            </Modal.Footer>
        </Modal >
    );
};

export default BulkCreatePartner;
